import React from 'react';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { useCustomerQueries } from '@dashboard/hooks/queries/useCustomerQueries';
import usePage from '@dashboard/hooks/usePage';

import Spinner from '@dashboard/components/Spinner';
import CardListItem from '@dashboard/components/CardListItem';
import EmptyState from '@dashboard/components/EmptyState';
import TableRowElement from '@dashboard/components/TableRowElement';

export default function CustomersList({ sort, filter = {}, search }) {
  const { page } = usePage();
  const { sellerId } = useAuth();
  const { getCustomersByCheckout } = useCustomerQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['pageCustomers', sellerId, sort, search, filter, page._id],
    queryFn: async ({ pageParam }) =>
      await getCustomersByCheckout(page._id, {
        ...filter,
        search,
        limit: 10,
        skip: pageParam * 10,
        sort: JSON.stringify(sort),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  let title = 'customers';
  if (page.type === 'form') {
    title = 'leads';
  } else if (page.type === 'event') {
    title = 'guests';
  }

  if (isLoading) {
    return <Spinner className="m-4" />;
  }

  if (isError) {
    return <div className="m-4">Unable to show {title}. Please refresh or contact support.</div>;
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty) {
    return (
      <EmptyState>
        <EmptyState.Title>No {title} yet</EmptyState.Title>
        <EmptyState.Subtitle>Your {title} will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  return (
    <div>
      <BottomScrollListener onBottom={() => fetchNextPage()}>
        {data?.pages?.map(data =>
          data?.data.map(customer => <CustomerListItem key={customer._id} customer={customer} />),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="m-4" />}
    </div>
  );
}

const CustomerListItem = ({ customer }) => (
  <CardListItem href={`/customers/${customer._id}`} key={customer._id}>
    <TableRowElement size={'3xl'}>
      <div className="font-medium">{customer.email || ''}</div>
      <div className="text-sm text-gray-500">{customer.name || ''}</div>
    </TableRowElement>

    <TableRowElement size="xl" align="right">
      {format(new Date(customer.createdAt), 'd MMM, yyyy, HH:mm')}
    </TableRowElement>
  </CardListItem>
);
